import { API_BASE_URL } from "./config";
import type { AuthUser } from "./types";

const TOKEN_KEY = "geosurvey_admin_token";
const USER_KEY = "geosurvey_admin_user";
const IDLE_KEY = "geosurvey_admin_idle_minutes";
const ACTIVITY_KEY = "geosurvey_admin_last_activity";

/** Fallback for a session signed in before the server sent its idle limit. */
const DEFAULT_IDLE_MINUTES = 600;

export function getToken(): string | null {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): AuthUser | null {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as AuthUser) : null;
}

function setSession(token: string, user: AuthUser, idleMinutes: number | null): void {
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(IDLE_KEY, String(idleMinutes ?? 0));
  markActivity();
}

export function clearSession(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(IDLE_KEY);
  localStorage.removeItem(ACTIVITY_KEY);
}

/** Minutes without use before the portal signs out; 0 = never. */
export function idleMinutes(): number {
  try {
    const raw = localStorage.getItem(IDLE_KEY);
    if (raw === null) return DEFAULT_IDLE_MINUTES;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return DEFAULT_IDLE_MINUTES;
  }
}

/** Records that the person used the portal. Shared by every open tab, so
 *  working in one tab keeps the others signed in too. */
export function markActivity(): void {
  try {
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
  } catch {
    // storage unavailable -- the server's own idle limit still applies
  }
}

/**
 * Tells the server the session is in use. The server's own idle limit counts
 * API requests, and someone reading the map or the table makes none -- so
 * without this it would end a session the person is actively working in.
 * Fire-and-forget: a failure here changes nothing.
 */
export function keepAlive(): void {
  const token = getToken();
  if (!token) return;
  void fetch(`${API_BASE_URL}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: "no-store",
  }).catch(() => undefined);
}

export function lastActivity(): number {
  try {
    const n = Number(localStorage.getItem(ACTIVITY_KEY));
    return Number.isFinite(n) && n > 0 ? n : Date.now();
  } catch {
    return Date.now();
  }
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const res = await fetch(`${API_BASE_URL}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ username, password, client: "portal" }),
  });

  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    // 423 (locked after wrong passwords) and the other refusals carry the
    // sentence to show in detail.message; plain errors in detail.
    throw new Error(
      res.status === 401 ? "Incorrect username or password" : messageFromBody(body) ?? body
    );
  }

  const data = await res.json();
  setSession(data.access_token, data.user, data.idle_timeout_minutes ?? null);
  return data.user as AuthUser;
}

/** The sentence in a FastAPI error body: {"detail": "..."} or
 *  {"detail": {"code": ..., "message": "..."}}. */
function messageFromBody(body: string): string | null {
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.detail === "string") return parsed.detail;
    if (typeof parsed?.detail?.message === "string") return parsed.detail.message;
  } catch {
    // not JSON
  }
  return null;
}

const SIGN_OUT_REASON_KEY = "geosurvey_admin_signout_reason";

/**
 * Keeps why the server refused the session (force logout, password changed,
 * expired) so the login page can say it, instead of an unexplained bounce.
 */
export function rememberSignOutReason(body: string): void {
  const message = messageFromBody(body);
  if (!message || message === "Could not validate credentials") return;
  try {
    sessionStorage.setItem(SIGN_OUT_REASON_KEY, message);
  } catch {
    // storage unavailable -- the login page just shows no reason
  }
}

/** Same, for a sign-out the portal decides itself (inactivity). */
export function rememberSignOutMessage(message: string): void {
  try {
    sessionStorage.setItem(SIGN_OUT_REASON_KEY, message);
  } catch {
    // storage unavailable
  }
}

export function readSignOutReason(): string | null {
  try {
    return sessionStorage.getItem(SIGN_OUT_REASON_KEY);
  } catch {
    return null;
  }
}

export function clearSignOutReason(): void {
  try {
    sessionStorage.removeItem(SIGN_OUT_REASON_KEY);
  } catch {
    // nothing stored
  }
}

export function logout(): void {
  endSession();
  if (typeof window !== "undefined") {
    window.location.href = "/login";
  }
}

/** Signs out without leaving the page -- for a message shown first (see IdleWatcher). */
export function endSession(): void {
  // Ends the session on the server too, so the token stops working at once.
  // Fire-and-forget: signing out locally must not wait on, or fail with, the
  // network.
  const token = getToken();
  if (token) {
    void fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: true,
    }).catch(() => undefined);
  }
  clearSession();
}
